import { useCallback, useState } from 'react';
import { DropTargetMonitor, XYCoord } from 'react-dnd';
import { format } from 'date-fns-tz';

import { useSaveCalendarEvent } from '~/Screens/Calendar/CalendarView/hooks';
import {
  ICalendarEventBookings,
  ICalendarOffset,
  IDraggableEvent,
  IPositionedEvent,
} from '~/Screens/Calendar/interfaces';
import {
  calculateNewEvent,
  getEventTimeInSeconds,
  IGetEventPosition,
} from '~/Screens/Calendar/CalendarView/components';
import { IEvent } from '~/Models/DTO/Event.model';
import { useCalendarEventBookings } from './useCalendarEventBookings';
import { EditEventOption } from '~/Screens/Calendar/constants';
import { useSaveCalendarEventSeries } from '~/Screens/Calendar/CalendarView/hooks/useSaveCalendarEventSeries';

interface IUseDropEvent {
  to: string | null;
  from: string | null;
  offset: ICalendarOffset;
  getEventPosition: IGetEventPosition;
}

export const useUpdateCalendarEvent = ({ offset, getEventPosition, from, to }: IUseDropEvent) => {
  const [updatingEvent, setUpdatingEvent] = useState<null | IPositionedEvent>(null);
  const [confirmationOptions, setConfirmationOptions] = useState<ICalendarEventBookings | void>();

  const { eventAction } = useSaveCalendarEvent();
  const { eventBookings } = useCalendarEventBookings();
  const { eventSeriesAction } = useSaveCalendarEventSeries();
  const onCloseEditEvent = useCallback(() => {
    setConfirmationOptions();
    setUpdatingEvent(null);
  }, [setConfirmationOptions, setUpdatingEvent]);

  const updateEvent = useCallback(
    (item: IDraggableEvent, monitor: DropTargetMonitor) => {
      const { event } = item;
      const position = getEventPosition({
        item,
        differenceFromInitialOffset: monitor.getDifferenceFromInitialOffset() as XYCoord,
      });
      const newEvent = calculateNewEvent({
        time: getEventTimeInSeconds(item.event),
        event,
        position,
        container: offset,
      });
      setUpdatingEvent({
        event: newEvent,
        position: { ...position, daysOffset: 0 },
        isLoading: true,
      });
      eventBookings.mutate(
        { event, newEvent },
        {
          onSuccess: setConfirmationOptions,
          onError: onCloseEditEvent,
        },
      );
    },
    [offset, getEventPosition, setUpdatingEvent, setConfirmationOptions, eventBookings.mutate],
  );

  const onConfirmEditEvent = useCallback(
    (selectedEditEventOption: EditEventOption) => {
      if (!updatingEvent) {
        return;
      }
      setConfirmationOptions();
      const { event } = updatingEvent as { event: IEvent };
      if (selectedEditEventOption === EditEventOption.EVENT_SERIES) {
        eventSeriesAction.mutate(
          { ...event, from, to },
          {
            onSuccess: (updatedEvent) => {
              const isEventTimeWasUpdated =
                updatedEvent &&
                format(new Date(event.start), 'HH:mm:ss') ===
                  format(new Date(updatedEvent.start), 'HH:mm:ss');
              if (!isEventTimeWasUpdated) {
                eventAction.mutate(event, {
                  onSettled: onCloseEditEvent,
                });
              }
              onCloseEditEvent();
            },
            onError: onCloseEditEvent,
          },
        );
        return;
      }
      eventAction.mutate(event, {
        onSettled: onCloseEditEvent,
      });
    },
    [onCloseEditEvent, updatingEvent, onCloseEditEvent, eventAction.mutate, from, to],
  );

  return {
    updateEvent,
    updatingEvent,
    onCloseEditEvent,
    onConfirmEditEvent,
    confirmationOptions,
  };
};
